import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { adminApi } from "../../api";
import ViewAsButton from "../../components/admin/ViewAsButton";

function VenueAvatar({ venue }) {
  const [broken, setBroken] = useState(false);
  if (venue.logo_url && !broken) {
    return (
      <div
        className="venue-avatar"
        style={{ background: venue.primary_color || "#3b82f6", width: 38, height: 38, borderRadius: 8, flexShrink: 0, padding: 0, overflow: "hidden" }}
      >
        <img
          src={venue.logo_url}
          alt={venue.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={() => setBroken(true)}
        />
      </div>
    );
  }
  return (
    <div
      className="venue-avatar"
      style={{ background: venue.primary_color || "#3b82f6", width: 38, height: 38, fontSize: "0.9rem", flexShrink: 0 }}
    >
      {venue.name[0]}
    </div>
  );
}

export default function AdminVenues() {
  const [venues, setVenues] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getVenues().then(async (v) => {
      setVenues(v);
      const counts = {};
      await Promise.all(
        v.map((venue) =>
          adminApi.getMembers(venue.id, { limit: 1, offset: 0 })
            .then((r) => { counts[venue.id] = r.total; })
            .catch(() => { counts[venue.id] = 0; })
        )
      );
      setMemberCounts(counts);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = venues.filter(
    (v) =>
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      v.slug.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = venues.filter((v) => v.active).length;
  const inactiveCount = venues.length - activeCount;

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="venues-page-header">
        <div>
          <h1 className="venues-page-title">Venues</h1>
          <p className="page-subtitle">Manage your venue locations and configurations</p>
        </div>
        <Link to="/admin/venues/new" className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Venue
        </Link>
      </div>

      {/* Summary pills */}
      {!loading && (
        <div className="venues-summary">
          <div className="venues-summary-pill">
            <span className="venues-summary-val">{venues.length}</span>
            <span className="venues-summary-lbl">Total</span>
          </div>
          <div className="venues-summary-divider" />
          <div className="venues-summary-pill">
            <span className="venues-summary-val" style={{ color: "var(--success)" }}>{activeCount}</span>
            <span className="venues-summary-lbl">Active</span>
          </div>
          <div className="venues-summary-divider" />
          <div className="venues-summary-pill">
            <span className="venues-summary-val" style={{ color: inactiveCount > 0 ? "var(--danger)" : "var(--text-muted)" }}>{inactiveCount}</span>
            <span className="venues-summary-lbl">Inactive</span>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search venues…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <h3>{search ? "No matching venues" : "No venues yet"}</h3>
          <p>{search ? "Try a different search term" : "Create your first venue to get started"}</p>
          {!search && <Link to="/admin/venues/new" className="btn btn-primary">Create Venue</Link>}
        </div>
      ) : (
        <div className="venues-table-wrap">
          {/* Table header */}
          <div className="venues-table-head">
            <span className="vt-col-venue">Venue</span>
            <span className="vt-col-members">Members</span>
            <span className="vt-col-status">Status</span>
            <span className="vt-col-created">Created</span>
            <span className="vt-col-actions">Actions</span>
          </div>

          {filtered.map((v) => (
            <div key={v.id} className="venues-table-row">
              {/* Venue identity */}
              <div className="vt-col-venue">
                <VenueAvatar venue={v} />
                <div className="vt-venue-info">
                  <span className="vt-venue-name">{v.name}</span>
                  <span className="vt-venue-slug">/{v.slug}</span>
                </div>
              </div>

              {/* Members */}
              <div className="vt-col-members">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13" style={{ flexShrink: 0, color: "var(--text-muted)" }}>
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                </svg>
                <span>{memberCounts[v.id] !== undefined ? memberCounts[v.id].toLocaleString() : "—"}</span>
              </div>

              {/* Status */}
              <div className="vt-col-status">
                <span className={`badge ${v.active ? "active" : "inactive"}`}>
                  <span className={`status-dot ${v.active ? "active" : "inactive"}`} />
                  {v.active ? "Active" : "Inactive"}
                </span>
              </div>

              {/* Created */}
              <div className="vt-col-created">
                {v.created_at ? new Date(v.created_at).toLocaleDateString("en-AU") : "—"}
              </div>

              {/* Actions */}
              <div className="vt-col-actions">
                <Link to={`/admin/venues/${v.id}`} className="btn btn-primary btn-sm">Manage</Link>
                <a href={`/${v.slug}`} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">Signup ↗</a>
                <Link to={`/portal/${v.slug}`} className="btn btn-purple btn-sm">Portal</Link>
                <ViewAsButton venueId={v.id} />
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
